import { useEffect, useRef, type MutableRefObject } from "react";

type EscapeHandler = {
  id: number;
  onEscapeRef: MutableRefObject<() => void>;
  enabledRef: MutableRefObject<boolean>;
};

const handlerStack: EscapeHandler[] = [];
let nextId = 1;
let listenerAttached = false;

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== "Escape") return;
  const current = handlerStack[handlerStack.length - 1];
  if (!current || !current.enabledRef.current) return;
  event.preventDefault();
  event.stopPropagation();
  current.onEscapeRef.current();
}

function attachListener() {
  if (listenerAttached || typeof document === "undefined") return;
  document.addEventListener("keydown", handleKeydown);
  listenerAttached = true;
}

function detachListener() {
  if (!listenerAttached || handlerStack.length > 0 || typeof document === "undefined") return;
  document.removeEventListener("keydown", handleKeydown);
  listenerAttached = false;
}

export function useEscapeStack(enabled: boolean, onEscape: () => void) {
  const onEscapeRef = useRef(onEscape);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    onEscapeRef.current = onEscape;
  }, [onEscape]);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof document === "undefined") return;
    const entry: EscapeHandler = { id: nextId++, onEscapeRef, enabledRef };
    handlerStack.push(entry);
    attachListener();

    return () => {
      const index = handlerStack.findIndex((handler) => handler.id === entry.id);
      if (index !== -1) handlerStack.splice(index, 1);
      detachListener();
    };
  }, [enabled]);
}
